import { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

const FOCUS_OPTIONS = ["Women-only", "Women-focused", "Women-friendly", "Co-ed"];

const blankForm = (prefill = {}) => ({
    studio_name: prefill.studio_name || "",
    address: prefill.address || "",
    place_id: prefill.id?.startsWith?.("google_") ? prefill.id.replace("google_", "") : "",
    curated_id: prefill.id && !prefill.id.startsWith?.("google_") ? prefill.id : "",
    studio_website: prefill.website_url || "",
    owner_name: "",
    owner_email: "",
    instagram: prefill.instagram || "",
    women_focus: "",
    classes_offered: "",
    schedule: "",
    intro_offer: "",
    notes: "",
});

export function ClaimStudioModal({ open, onClose, prefill = {} }) {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState(() => blankForm(prefill));

    // Re-hydrate the form every time the modal is reopened with new prefill data.
    // (useState initializer only runs on first mount, so without this effect
    // a second open with a different prefill keeps stale form state.)
    useEffect(() => {
        if (open) {
            setForm(blankForm(prefill));
            setSubmitted(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, prefill?.id, prefill?.studio_name]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Domain match preview — if the owner email is from the studio's own domain,
    // surface that they'll be auto-verified on submit (no manual queue).
    const willAutoVerify = (() => {
        if (!form.owner_email || !form.studio_website) return false;
        const ed = form.owner_email.split("@")[1]?.toLowerCase().trim();
        try {
            const u = form.studio_website.includes("://") ? form.studio_website : `http://${form.studio_website}`;
            let wd = new URL(u).hostname.toLowerCase();
            if (wd.startsWith("www.")) wd = wd.slice(4);
            return !!ed && !!wd && (ed === wd || ed.endsWith("." + wd));
        } catch {
            return false;
        }
    })();

    const submit = async () => {
        if (!form.owner_name.trim() || !form.owner_email.trim() || !form.women_focus) {
            toast.error("Name, email, and audience focus are required.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/claims", form);
            setSubmitted(true);
            toast.success("Thanks! We'll review your claim within 48 hours.");
        } catch (e) {
            console.warn(e);
            toast.error(e?.response?.data?.detail || "Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const close = () => {
        setSubmitted(false);
        onClose?.();
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && close()}>
            <DialogContent
                className="rounded-none max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto border-foreground bg-background p-0"
                data-testid="claim-modal"
            >
                <DialogHeader className="p-6 md:p-10 pb-2 text-left">
                    <p className="font-mono-label text-[10px] text-muted-foreground mb-2">
                        For studio owners
                    </p>
                    <DialogTitle className="font-display text-3xl md:text-4xl leading-tight">
                        Claim & <span className="font-editorial text-primary">verify</span> your studio.
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground mt-2">
                        Submit the info below. Once approved, you get a Verified badge and
                        higher placement on the map. Free, no card required.
                    </DialogDescription>
                </DialogHeader>

                {submitted ? (
                    <div className="p-6 md:p-10 flex flex-col items-start gap-4">
                        <CheckCircle2 className="h-10 w-10 text-primary" />
                        <h3 className="font-display text-2xl">
                            {willAutoVerify ? "Verified instantly." : "Submitted."}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                            {willAutoVerify ? (
                                <>
                                    Your email matches <strong>{form.studio_website}</strong> — your
                                    listing is now <strong>Verified</strong> and ranks above
                                    unverified competitors on the map.
                                </>
                            ) : (
                                <>
                                    We'll review and email <strong>{form.owner_email}</strong> within
                                    48 hours. You can submit additional details anytime.
                                </>
                            )}
                        </p>
                        <Button
                            onClick={close}
                            className="rounded-none border border-foreground bg-foreground text-background uppercase tracking-widest hover-invert"
                            data-testid="claim-close-btn"
                        >
                            Done
                        </Button>
                    </div>
                ) : (
                    <div className="p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                        <Field label="Studio name *" testid="claim-studio">
                            <Input
                                value={form.studio_name}
                                onChange={(e) => set("studio_name", e.target.value)}
                                className="rounded-none border-foreground"
                            />
                        </Field>
                        <Field label="Address" testid="claim-address">
                            <Input
                                value={form.address}
                                onChange={(e) => set("address", e.target.value)}
                                className="rounded-none border-foreground"
                            />
                        </Field>
                        <Field label="Your name *" testid="claim-name">
                            <Input
                                value={form.owner_name}
                                onChange={(e) => set("owner_name", e.target.value)}
                                className="rounded-none border-foreground"
                            />
                        </Field>
                        <Field label="Your email *" testid="claim-email">
                            <Input
                                type="email"
                                value={form.owner_email}
                                onChange={(e) => set("owner_email", e.target.value)}
                                className="rounded-none border-foreground"
                            />
                        </Field>
                        <Field label="Audience focus *" testid="claim-focus">
                            <div className="flex flex-wrap gap-2">
                                {FOCUS_OPTIONS.map((o) => (
                                    <button
                                        type="button"
                                        key={o}
                                        onClick={() => set("women_focus", o)}
                                        className={`px-3 py-2 font-mono-label text-[10px] border border-foreground hover-invert ${
                                            form.women_focus === o
                                                ? "bg-foreground text-background"
                                                : "bg-background"
                                        }`}
                                        data-testid={`claim-focus-${o}`}
                                    >
                                        {o}
                                    </button>
                                ))}
                            </div>
                        </Field>
                        <Field label="Instagram URL" testid="claim-instagram">
                            <Input
                                value={form.instagram}
                                onChange={(e) => set("instagram", e.target.value)}
                                className="rounded-none border-foreground"
                                placeholder="https://instagram.com/yourstudio"
                            />
                        </Field>
                        <Field label="Classes offered" testid="claim-classes" full>
                            <Input
                                value={form.classes_offered}
                                onChange={(e) => set("classes_offered", e.target.value)}
                                className="rounded-none border-foreground"
                                placeholder="e.g. Hot Vinyasa, Reformer Pilates, Prenatal Yoga"
                            />
                        </Field>
                        <Field label="Schedule summary" testid="claim-schedule" full>
                            <Input
                                value={form.schedule}
                                onChange={(e) => set("schedule", e.target.value)}
                                className="rounded-none border-foreground"
                                placeholder="e.g. Mon–Fri 6am–8pm, weekends 8am–noon"
                            />
                        </Field>
                        <Field label="Intro offer (optional)" testid="claim-intro" full>
                            <Input
                                value={form.intro_offer}
                                onChange={(e) => set("intro_offer", e.target.value)}
                                className="rounded-none border-foreground"
                                placeholder="e.g. First class free; 2-week trial $30"
                            />
                        </Field>
                        <Field label="Anything else?" testid="claim-notes" full>
                            <Textarea
                                value={form.notes}
                                onChange={(e) => set("notes", e.target.value)}
                                className="rounded-none border-foreground min-h-[80px]"
                                placeholder="Amenities, accessibility, anything else we should know."
                            />
                        </Field>

                        <div className="md:col-span-2 flex items-center justify-between border-t border-border pt-5 mt-2 gap-4 flex-wrap">
                            <p className="text-xs text-muted-foreground max-w-md">
                                {willAutoVerify ? (
                                    <span className="inline-flex items-center gap-2 text-primary font-bold uppercase tracking-widest">
                                        <CheckCircle2 className="h-4 w-4" /> Domain match — you'll be auto-verified
                                    </span>
                                ) : (
                                    "By submitting, you confirm you represent this studio. Approval is human-reviewed; we may email to verify."
                                )}
                            </p>
                            <Button
                                onClick={submit}
                                disabled={submitting}
                                className="rounded-none border border-foreground bg-foreground text-background uppercase tracking-widest hover-invert"
                                data-testid="claim-submit-btn"
                            >
                                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {willAutoVerify ? "Submit & Verify" : "Submit claim"}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children, testid, full = false }) {
    return (
        <div className={full ? "md:col-span-2" : ""} data-testid={testid}>
            <Label className="font-mono-label text-[10px] text-muted-foreground block mb-2">
                {label}
            </Label>
            {children}
        </div>
    );
}
